<template>
  <div class="folder-grid">
    <v-container fluid grid-list-md>
      <v-layout row wrap>
        <v-flex
          v-for="folder in folders"
          :key="folder.id"
          xs12 sm6 md4 lg3
          class="folder-grid-item"
        >
          <v-card
            class="folder-grid-card"
            :class="[folderNotReceivedClass(folder)]"
            @click.stop="openFolder(folder.id)"
            hover
            :elevation="2"
          >
            <v-card-title class="folder-header">
              <div class="process-info-container w-100">
                <img
                  class="folder-icon mr-2"
                  :src="folderIcon(folder)"
                  alt="Ícone de Pasta"
                >
                <div class="process-number-container">
                  <span class="process-number">{{ folder.process_number }}</span>
                  <v-btn icon x-small class="copy-button ml-1" @click.stop="copyToClipboard(folder.process_number)">
                    <v-icon size="14">mdi-content-copy</v-icon>
                  </v-btn>
                </div>

                <!-- Unidade Administrativa -->
                <v-chip
                  v-if="folder.current_owner && folder.current_owner.administrative_unit"
                  x-small
                  class="admin-unit-badge primary mb-2"
                  :style="{
                    '--admin-unit-badge-bg': getUnitColor(folder.current_owner.administrative_unit),
                    '--admin-unit-badge-color': 'white',
                    '--admin-unit-badge-border': getUnitColor(folder.current_owner.administrative_unit)
                  }"
                >
                  {{ folder.current_owner.administrative_unit.abbreviation || folder.current_owner.administrative_unit.name }}
                </v-chip>

                <!-- Unidades adicionais -->
                <template v-if="folder.current_owner && folder.current_owner.administrative_units && folder.current_owner.administrative_units.length > 0">
                  <v-chip
                    v-for="unit in getFilteredAdminUnits(folder.current_owner.administrative_units, folder.current_owner.administrative_unit ? folder.current_owner.administrative_unit.id : null)"
                    :key="unit.id"
                    x-small
                    outline
                    class="admin-unit-badge secondary"
                    :style="{
                      '--admin-unit-badge-color': getUnitColor(unit),
                      '--admin-unit-badge-border': getUnitColor(unit)
                    }"
                  >
                    {{ unit.abbreviation || unit.name }}
                  </v-chip>
                </template>
              </div>
            </v-card-title>

            <v-divider></v-divider>

            <v-card-text class="grid-content">
              <v-layout column>
                <!-- Tipo de pasta com tooltip -->
                <v-flex class="folder-type mb-2">
                  <v-tooltip bottom max-width="300">
                    <template v-slot:activator="{ on }">
                      <v-chip small label class="folder-type-name" v-on="on">
                        {{ folder.folder_type.name }}
                      </v-chip>
                    </template>
                    <span class="custom-tooltip">{{ folder.folder_type.name }}</span>
                  </v-tooltip>
                </v-flex>

                <!-- Entidade com tooltip -->
                <v-flex class="mb-2">
                  <v-tooltip bottom max-width="300">
                    <template v-slot:activator="{ on }">
                      <v-chip color="grey-lighten-2 entity-chip" v-on="on" class="text-truncate">
                        <v-avatar size="16" color="white">
                          <v-icon size="16" color="grey">mdi-briefcase</v-icon>
                        </v-avatar>
                        <strong class="grey--text text--darken-2 text-truncate">{{ folder.entity.name }}</strong>
                      </v-chip>
                    </template>
                    <span class="custom-tooltip">{{ folder.entity.name }}</span>
                  </v-tooltip>
                </v-flex>

                <!-- Assunto (limitado) com tooltip -->
                <v-flex v-if="folder.subject" class="subject-text mt-2">
                  <v-tooltip bottom max-width="300">
                    <template v-slot:activator="{ on }">
                      <div v-on="on" class="text-truncate">{{ folder.subject }}</div>
                    </template>
                    <span class="custom-tooltip">{{ folder.subject }}</span>
                  </v-tooltip>
                </v-flex>
              </v-layout>
            </v-card-text>

            <v-divider></v-divider>

            <v-card-actions class="grid-footer">
              <v-layout align-center justify-space-between fill-height wrap>
                <!-- Proprietário atual -->
                <v-flex xs6>
                  <v-tooltip bottom max-width="300">
                    <template v-slot:activator="{ on, attrs }">
                      <v-layout align-center v-bind="attrs" v-on="on">
                        <v-avatar size="24" class="mr-2">
                          <img
                            v-if="folder.current_owner && folder.current_owner.avatar && folder.current_owner.avatar.url"
                            :src="folder.current_owner.avatar.url"
                            alt="Avatar"
                          >
                          <span v-else class="white--text">{{ getInitials(folder.current_owner) }}</span>
                        </v-avatar>
                        <span class="caption truncate-text" style="max-width: 80px;">{{ shortOwnerName(folder.current_owner) }}</span>
                      </v-layout>
                    </template>
                    <span class="custom-tooltip">{{ folder.current_owner ? folder.current_owner.name : 'Sem proprietário' }}</span>
                  </v-tooltip>
                </v-flex>

                <!-- Prazo -->
                <v-flex xs6 class="text-right">
                  <v-layout align-center justify-end>
                    <v-icon v-if="!isEmpty(folder.limit_date)" size="18" class="mr-1">mdi-clock-fast</v-icon>
                    <span class="caption"><term-component :folder="folder"></term-component></span>
                  </v-layout>
                </v-flex>

                <!-- Status Badge - Somente mostrado quando necessário -->
                <v-flex xs12 class="mt-2 text-center" v-if="folder.received_status || folder.pending_type">
                  <span
                    v-if="folder.received_status === 'Recebido'"
                    class="status-badge received"
                  >
                    Recebido
                  </span>
                  <span
                    v-else-if="folder.received_status === 'Não Recebido'"
                    class="status-badge waiting"
                  >
                    Aguardando
                  </span>
                  <span
                    v-else-if="folder.received_status === 'Recusado'"
                    class="status-badge refused"
                  >
                    Recusado
                  </span>
                  <span
                    v-else-if="folder.pending_type === 'review'"
                    class="status-badge pending"
                  >
                    Revisão
                  </span>
                  <span
                    v-else-if="folder.pending_type === 'signature'"
                    class="status-badge pending"
                  >
                    Assinatura
                  </span>
                  <span
                    v-else
                    class="status-badge waiting"
                  >
                    Aguardando
                  </span>
                </v-flex>
              </v-layout>
            </v-card-actions>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import { isEmpty } from 'lodash';
import TermComponent from '@/components/inbox/TermComponent';
import { mapGetters } from 'vuex';
import adminUnitHandling from '@/mixins/admin-unit-handling';

export default {
  name: 'FolderGrid',
  components: {
    TermComponent
  },
  mixins: [adminUnitHandling],
  props: {
    folders: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      isEmpty
    };
  },
  computed: {
    ...mapGetters(['user'])
  },
  methods: {
    folderNotReceivedClass(folder) {
      if (folder.current_owner && this.user && folder.current_owner.id === this.user.id) {
        return 'received-folder';
      }
      return '';
    },
    folderIcon(folder) {
      if (folder.current_owner && this.user && folder.current_owner.id === this.user.id) {
        return '/static/images/folder/folder-open-icon.svg';
      }
      return '/static/images/folder/folder-closed-icon.svg';
    },
    openFolder(id) {
      const route = this.$router.resolve(`/pastas/${id}/show`);
      window.open(route.href, '_blank');
    },
    // Método não mais necessário, pois agora usamos CSS para truncar textos longos
    // e tooltips para mostrar o conteúdo completo
    truncatedSubject(subject) {
      return subject || '';
    },
    getInitials(owner) {
      if (!owner || !owner.name) return '?';
      return owner.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    },
    shortOwnerName(owner) {
      if (!owner || !owner.name) return '';
      const nameParts = owner.name.split(' ');
      if (nameParts.length === 1) return nameParts[0];
      return `${nameParts[0]} ${nameParts[nameParts.length - 1]}`;
    },
    getUnitColor(unit) {
      // Implemente a lógica para obter a cor adequada com base no unit
      return '#000'; // Placeholder, você pode implementar uma lógica mais robusta
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$store.dispatch('toggleSnackbar', {
          show: true,
          timeout: 2000,
          color: 'success',
          message: 'Processo copiado para a área de transferência',
          messageIcon: 'mdi-check',
        });
      }).catch(() => {
        this.$store.dispatch('toggleSnackbar', {
          show: true,
          timeout: 2000,
          color: 'error',
          message: 'Erro ao copiar o Processo',
          messageIcon: 'mdi-close',
        });
      });
    }
  }
};
</script>

<style scoped lang="scss">
.folder-grid {
  .folder-grid-item {
    transition: transform 0.2s ease;

    &:hover {
      transform: translateY(-4px);
    }
  }

  .folder-grid-card {
    height: 100%;
    display: flex;
    flex-direction: column;
    transition: all 0.3s ease;
    border-radius: 8px;
    overflow: hidden;

    &:hover {
      box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15) !important;
    }

    .folder-header {
      padding: 8px 12px;
      background-color: #f8f9fa;
    }

    .folder-icon {
      height: 24px;
      width: 24px;
      transition: transform 0.2s ease;
    }

    .grid-content {
      flex-grow: 1;
      padding: 12px;
    }

    .grid-footer {
      padding: 6px 12px;
      background-color: #f8f9fa;
    }

    .process-info-container {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .process-number-container {
      display: flex;
      align-items: center;
      height: 16px;

      .process-number {
        font-size: 11px;
        color: #757575;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        display: block;
        cursor: default;
      }

      .copy-button {
        margin-top: -2px !important;
        height: 16px !important;
        width: 16px !important;

        &:hover {
          background-color: rgba(0, 0, 0, 0.05);
        }

        .v-icon {
          color: #757575;
        }
      }

      &:hover {
        .copy-button {
          opacity: 1;
        }
      }
    }

    .folder-type-name {
      font-size: 12px;
      color: #616161;
      margin-bottom: 4px;
    }

    .admin-unit-badge {
      margin-top: 4px;
    }

    .entity-chip {
      max-width: 100%;
      height: auto;
      padding: 4px 8px;
    }

    .subject-text {
      font-size: 0.875rem;
      color: #757575;
      line-height: 1.4;
      max-height: 2.8em;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    .status-badge {
      display: inline-flex;
      align-items: center;
      padding: 4px 12px;
      border-radius: 16px;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      will-change: transform;
      transform: translateZ(0);
      backface-visibility: hidden;
      line-height: 1.2;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
      border: 1px solid;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
      }

      .v-icon {
        font-size: 14px;
        transition: transform 0.3s ease;
      }

      &:hover .v-icon {
        transform: scale(1.2);
      }

      &.received {
        background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
        color: #2e7d32;
        border-color: #4CAF50;
      }

      &.waiting {
        background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
        color: #ef6c00;
        border-color: #FFC107;
      }

      &.refused {
        background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
        color: #c62828;
        border-color: #F44336;
      }

      &.pending {
        background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
        color: #1565c0;
        border-color: #2196F3;
      }
    }

    &.received-folder {
      border-left: 4px solid #2196F3;
      background-color: #f5f5f5;
    }
  }
}

// Responsividade
@media (max-width: 600px) {
  .folder-grid {
    .folder-grid-item {
      padding: 4px;
    }

    .folder-grid-card {
      .folder-header {
        padding: 6px 8px;
      }

      .grid-content {
        padding: 8px;
      }

      .grid-footer {
        padding: 4px 8px;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/folder/FolderGrid.vue